import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Card } from "primereact/card";
import PaymentKeysSettingsJunipay from './PaymentsKeysSettingsJunipay';
import { Button } from 'primereact/button';
import { useTranslation, Trans } from "react-i18next";
import BeginOnboardingRequestActions from "../../Redux/transition/beginOnboarding/beginOnboarding.reducer";
import { Toast } from "primereact/toast";
import TelecelCashLogo from "../../assets/images/Telecel-Cash-Logo.jpg";
import ATMoneyLogo from "../../assets/images/AT-LOGO.png";
import MTN_MomoLogo from "../../assets/images/momo.jpg";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "primereact/confirmdialog";
import Loading from "../../Components/Loading";
import AuthorizedSignatoryDialog from "../../Components/AuthorizedSignatoryDialog";
import { usePaymentProviderScreen } from "./usePaymentProviderScreen";
import "../../Styles/components/customToast.scss";

const PROVIDER = "JUNIPAY";

function ProviderDetailJunipay() {
    const formRef = useRef();

    const {
        business,
        businessId,
        paymentCredentials,
        onboardingRequestInProgress,
        failureOnboarding,
        isOnboardingInProgress,
        setIsOnboardingInProgress,
        activationUnlocked,
        providerStatus,
        isScreenLoading,
        refreshProviderState,
    } = usePaymentProviderScreen(PROVIDER);

    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive)

    const dispatch = useDispatch();
    const onboardingRequest = useCallback((id, paymentProvider) => { dispatch(BeginOnboardingRequestActions.beginOnboardingRequest(id, paymentProvider)) }, [dispatch]);

    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const id = businessId;
    const activateProviderToast = useRef(null);
    const testPaymentToast = useRef(null);

    const [displayActivateDialog, setDisplayActivateDialog] = useState(false);
    const [displaySignatoryDialog, setDisplaySignatoryDialog] = useState(false);

    const activateProviderErrorMessage = () => {
        activateProviderToast.current.show({ severity: 'error', summary: t("failed"), detail: t("api_settings.verification_started_error_info"), life: 5000 });
    }

    const activateProviderSuccessMessage = () => {
        activateProviderToast.current.show({ severity: 'success', summary: t("success"), detail: t("api_settings.verification_started_success_info"), life: 3000 });
    }

    useEffect(() => {
        if (isOnboardingInProgress && !onboardingRequestInProgress && !failureOnboarding) {
            activateProviderSuccessMessage();
        }
        if (isOnboardingInProgress && !onboardingRequestInProgress && failureOnboarding) {
            activateProviderErrorMessage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [failureOnboarding, onboardingRequestInProgress]);

    const acceptActivate = () => {
        onboardingRequest(id, PROVIDER);
        setIsOnboardingInProgress(true);
        setDisplaySignatoryDialog(false);
        setDisplayActivateDialog(false);
    }

    const openActivateFlow = () => {
        testPaymentToast.current?.clear();
        if (business?.customPmtProvider) {
            setDisplaySignatoryDialog(true);
        } else {
            setDisplayActivateDialog(true);
        }
    };

    const handleMoveToProducts = useCallback(() => {
        testPaymentToast.current?.clear();
        const groupData = planGroupsGraphedActive?.groups?.[0].group
        navigate(`/products/plan/new/${groupData.id}`, { state: { planGroup: groupData, plan: true } })
    }, [navigate, planGroupsGraphedActive?.groups]);

    const showJunipayTestPaymentToast = useCallback(() => {
        window.setTimeout(() => {
            testPaymentToast.current?.clear();
            testPaymentToast.current?.show({
                severity: "info",
                summary: t("business.test_payment"),
                detail: (
                    <div className="junipay-test-payment-toast-content">
                        <Trans 
                            i18nKey="business.pay_tip"
                            className='text-xs '
                            components={{1: <li />}}
                        />
                        <Button
                            className="buttom-red mt-2"
                            label={t("business.create_plan")}
                            onClick={handleMoveToProducts}
                        />
                    </div>
                ),
                sticky: true,
            });
        }, 0);
    }, [handleMoveToProducts, t]);

    const confirmActivateProviderButtons = (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
                label={t('common.no')}
                id="confirm_no_btn"
                onClick={() => { setDisplayActivateDialog(false) }}
            />
            <Button
                label={t('common.yes')}
                loading={onboardingRequestInProgress}
                id="confirm_yes_btn"
                onClick={() => {
                    acceptActivate()
                }}
                autoFocus
            />
        </div>
    )

    if (isScreenLoading) {
        return <Loading />;
    }

    return (
        <div>
            <Toast ref={activateProviderToast} className="custom-toast"
                onRemove={(message) => {
                    if (message.severity === "success") {
                        window.dispatchEvent(new Event("reveknew:clear-global-search"));
                    }
                    refreshProviderState();
                    setIsOnboardingInProgress(false);
                    setDisplayActivateDialog(false);
                    setTimeout(() => {
                        if (formRef.current) {
                            formRef.current.resetFormToEmpty();
                        }
                    }, 300);
                }}
            />

            <Toast ref={testPaymentToast} className="custom-toast junipay-test-payment-toast" position="top-right" baseZIndex={1100} />

            <ConfirmDialog id="tier_dialog" style={{ width: "min(350px, 90vw)" }}
                visible={displayActivateDialog}
                onHide={() => setDisplayActivateDialog(false)}
                message={t('idoc.confirm_activate_message')}
                footer={confirmActivateProviderButtons}
                header={t('idoc.activate_provider')} accept={acceptActivate}
                reject={() => setDisplayActivateDialog(false)}
            />

            <AuthorizedSignatoryDialog
                visible={displaySignatoryDialog}
                onHide={() => setDisplaySignatoryDialog(false)}
                onConfirm={acceptActivate}
                loading={onboardingRequestInProgress}
            />

            <Card className="payment_settings_main_card">
                <div className="settings_card_div">
                    <div className="grid" id="main_top_tags">
                        <div className="col-12 lg:col-6 left_tags_texts">
                            <Button onClick={() => navigate(-1)} id="back_btn">
                                <i className="pi pi-arrow-left backBtn-icon"></i>
                            </Button>
                            <div className="left_texts">
                                <p className="appsNmobile_txt">{t("api_settings.junipay_txt")}</p>
                                <p className="orchardPay_txt">{t("api_settings.Junipay")}</p>
                            </div>
                        </div>

                        <div className="col-12 lg:col-6 right-buttons">
                            {providerStatus === "NOT_ACTIVATED" &&
                                <span id="not_activated_label">{t("api_settings.not_activated_label")}</span>}

                            {providerStatus === "ACTIVE" &&
                                <span className="active_label">{t("api_settings.active_label")}</span>}

                            {providerStatus === "IN_PROGRESS" &&
                                <span className="in_progress_label">{t("api_settings.in_progress_label")} </span>}

                            {providerStatus === "NOT_ACTIVATED" &&
                                <Button
                                    id="activate_provider"
                                    onClick={openActivateFlow}>{onboardingRequestInProgress ? t("api_settings.activating") : t("api_settings.activate_provider")}
                                </Button>
                            }

                        </div>
                    </div>

                    <div className="border_about">
                        <div className="inner_div_about mb-3">
                            <div className="about_appsNmobile_div">
                                <p className="about_appsNmobile_p"> {t("api_settings.about_junipay")}</p>
                            </div>
                            <p className="about_appsNmobile_text1"> {t("api_settings.about_junipay_txt1")}</p>
                        </div>
                    </div>

                    <div className="div_payment_methods">
                        <p className="payment_settings_methods">{t("api_settings.payment_method_text")}</p>
                        <div className=" payment_methods_logos">
                            <img src={MTN_MomoLogo} alt="MTNMomo" />
                            <img src={ATMoneyLogo} alt="ATMoney" />
                            <img src={TelecelCashLogo} alt="TCashLogo" />
                        </div>
                    </div>

                    <p className="requirement_txt">{t("api_settings.requirement_txt")}</p>
                    <div className="grid hidden md:flex">
                        <div className="col-4">
                            <p className="general_doc_header">{t("api_settings.general_doc_header")}</p>
                        </div>

                        <div className="col-3">
                            <p className="unregistered_header">{t("api_settings.unregistered_header")}</p>
                        </div>

                        <div className="col-4">
                            <p className="special_notes_header">{t("api_settings.special_notes_header")}</p>
                        </div>

                    </div>

                    <hr className="paymentsSettings_hr hidden md:block" />

                    <div className="grid requirement_div">
                        <div className="col-12 md:col-4">
                            <p className="general_doc_header md:hidden">{t("api_settings.general_doc_header")}</p>
                            <p className="requirement_docs_color">{t("api_settings.general_doc_txt1")}</p>
                            <p className="requirement_docs_color">{t("api_settings.general_doc_txt2")}</p>
                            <p className="requirement_docs_color">{t("api_settings.general_doc_txt3")}</p>
                            <p className="requirement_docs_color">{t("api_settings.general_doc_txt4")}</p>
                        </div>

                        <div className="col-12 md:col-3">
                            <p className="unregistered_header md:hidden">{t("api_settings.unregistered_header")}</p>
                            <p className="unregistered_docs_color">{t("api_settings.unregistered_txt1")}</p>
                            <p className="unregistered_docs_color">{t("api_settings.unregistered_txt2")}</p>
                        </div>

                        <div className="col-12 md:col-4 special_notes_div">
                            <p className="special_notes_header md:hidden">{t("api_settings.special_notes_header")}</p>
                            <p className="special_notes_color">{t("api_settings.special_notes_info")}</p>
                        </div>
                    </div>

                    <hr className="paymentsSettings_hr" />

                    <div className="payment-main">
                        {!paymentCredentials ?
                            <div className="no_credentials_heading">
                                <p className='payment_heading1'>{t("business.payment_heading1")}</p>
                                <div className="payment_provider_heading">
                                    <p className="appsNmobile_no_paymentcredentials_heading"> {t("business.junipay_no_paymentcredentials_heading")}</p>
                                </div>
                            </div> :
                            <div>
                                <div className='payment_heading1'>{t("business.payment_heading1")}</div>

                                <div className="heading_main_div_payments">
                                    <Trans i18nKey="business.junipay_payment_heading1"
                                        components={{
                                            1: <p className="payment_heading"></p>,
                                            2: <p className="payment_purple_txt"></p>,
                                            3: <p className="payment_heading"></p>
                                        }} />
                                </div>
                            </div>
                        }
                        {paymentCredentials !== null && <div className='payment-added-alert col-12'><span className='payment-added-alert1'>
                            {t("business.existing_payment1")}</span> <br />
                            <span className='payment-added-alert2'>{t("business.existing_payment2")}</span>
                        </div>}
                        <PaymentKeysSettingsJunipay onCredentialsSaved={showJunipayTestPaymentToast} credentialsUnlocked={activationUnlocked} />
                    </div>
                </div>
            </Card >
        </div >
    )
}
export default ProviderDetailJunipay;
